import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/Badge/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ClientInfo } from '@certhon/models';
import { ChevronRight, Monitor } from 'lucide-react';
import { useState } from 'react';
import { formatDistanceToNow, format } from 'date-fns';
import { formatTimeCompact } from '@/utils/date';
import { cn } from '@/lib/utils';
import { UAParser } from 'ua-parser-js';

interface ClientsTableProps {
  clients?: ClientInfo[];
}

function parseUserAgent(ua: string | null): string {
  if (!ua) return 'Unknown';
  const parser = new UAParser(ua);
  const browser = parser.getBrowser();
  const os = parser.getOS();
  return `${browser.name || 'Unknown'} on ${os.name || 'Unknown'}`;
}

type AuthStatus = 'active' | 'idle' | 'expired' | 'none';

function formatAuthMethod(method: string | null): string {
  if (!method) return 'Unknown';
  const labels: Record<string, string> = {
    password: 'Password',
    tag: 'Tag',
    token: 'Token',
  };
  return labels[method] || method;
}

function getSessionStatus(client: ClientInfo): {
  status: AuthStatus;
  tooltip: string;
} {
  const now = Date.now();

  if (!client.access_token_expires && !client.refresh_token_expires) {
    return { status: 'none', tooltip: 'No session data' };
  }

  const accessExpires = client.access_token_expires
    ? new Date(client.access_token_expires).getTime()
    : 0;
  const refreshExpires = client.refresh_token_expires
    ? new Date(client.refresh_token_expires).getTime()
    : 0;

  // Build tooltip parts
  const tooltipParts: string[] = [];

  // Auth method
  tooltipParts.push(`Method: ${formatAuthMethod(client.auth_method)}`);

  // Session created
  if (client.session_created) {
    tooltipParts.push(
      `Created: ${format(new Date(client.session_created), 'PPp')}`,
    );
  }

  if (refreshExpires && refreshExpires < now) {
    tooltipParts.push(
      `Expired: ${format(new Date(client.refresh_token_expires!), 'PPp')}`,
    );
    return {
      status: 'expired',
      tooltip: tooltipParts.join('\n'),
    };
  }

  if (client.refresh_token_expires) {
    tooltipParts.push(
      `Refresh expires: ${format(new Date(client.refresh_token_expires), 'PPp')}`,
    );
  }

  if (accessExpires && accessExpires > now) {
    tooltipParts.push(
      `Access expires: ${format(new Date(client.access_token_expires!), 'p')}`,
    );
    return {
      status: 'active',
      tooltip: tooltipParts.join('\n'),
    };
  }

  return {
    status: 'idle',
    tooltip: tooltipParts.join('\n'),
  };
}

function SessionStatusBadge({ client }: { client: ClientInfo }) {
  const { status, tooltip } = getSessionStatus(client);

  if (status === 'none') {
    return <span className="text-muted-foreground">—</span>;
  }

  const variants: Record<AuthStatus, { color: string; label: string }> = {
    active: { color: 'bg-green-500', label: 'Active' },
    idle: { color: 'bg-yellow-500', label: 'Idle' },
    expired: { color: 'bg-red-500', label: 'Expired' },
    none: { color: '', label: '' },
  };

  const { color, label } = variants[status];

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex cursor-help items-center gap-1.5">
          <span className={cn('h-2 w-2 rounded-full', color)} />
          <span className="hidden text-sm sm:inline">{label}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent className="whitespace-pre-line">{tooltip}</TooltipContent>
    </Tooltip>
  );
}

function SyncStatusBadge({ client }: { client: ClientInfo }) {
  if (client.sync_success === null) {
    return <span className="text-muted-foreground">—</span>;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge variant={client.sync_success ? 'default' : 'destructive'}>
          <span>{client.sync_success ? '✓' : '✗'}</span>
          <span className="ml-1 hidden sm:inline">
            {client.sync_success ? 'Synced' : 'Failed'}
          </span>
        </Badge>
      </TooltipTrigger>
      <TooltipContent>
        {client.sync_date && (
          <div>
            Last sync:{' '}
            {formatDistanceToNow(new Date(client.sync_date), {
              addSuffix: true,
            })}
          </div>
        )}
        {client.sync_meta?.message && (
          <div className="text-destructive-foreground">
            {client.sync_meta.message}
          </div>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

function LastContactCell({ date }: { date: string }) {
  const d = new Date(date);
  const fullText = formatDistanceToNow(d, { addSuffix: true });
  const compactText = formatTimeCompact(d);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="text-muted-foreground cursor-help">
          <span className="sm:hidden">{compactText}</span>
          <span className="hidden sm:inline">{fullText}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent>{format(d, 'PPpp')}</TooltipContent>
    </Tooltip>
  );
}

export function ClientsTable({ clients = [] }: ClientsTableProps) {
  const [open, setOpen] = useState(false);

  // Count only clients with actual sync data
  const syncedClients = clients.filter((c) => c.sync_success !== null);
  const successCount = syncedClients.filter(
    (c) => c.sync_success === true,
  ).length;
  const failCount = syncedClients.filter(
    (c) => c.sync_success === false,
  ).length;

  // Count active sessions
  const activeSessionCount = clients.filter(
    (c) =>
      getSessionStatus(c).status === 'active' ||
      getSessionStatus(c).status === 'idle',
  ).length;

  const summaryParts: string[] = [];
  if (clients.length > 0) {
    summaryParts.push(
      `${clients.length} client${clients.length !== 1 ? 's' : ''}`,
    );
  }
  if (activeSessionCount > 0) {
    summaryParts.push(`${activeSessionCount} active`);
  }
  if (successCount > 0) {
    summaryParts.push(`${successCount} synced`);
  }
  if (failCount > 0) {
    summaryParts.push(`${failCount} failing`);
  }

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="rounded-lg border"
    >
      <CollapsibleTrigger className="hover:bg-muted/50 flex w-full flex-wrap items-center gap-2 p-3">
        <ChevronRight
          className={cn('h-4 w-4 transition-transform', open && 'rotate-90')}
        />
        <Monitor className="h-4 w-4" />
        <span className="font-medium">Clients</span>
        <span
          className="text-muted-foreground basis-full text-left text-sm sm:ml-auto sm:basis-auto
            sm:text-right"
        >
          {summaryParts.length > 0 ? summaryParts.join(' • ') : 'None'}
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent className="px-3 pb-3">
        {clients.length === 0 ? (
          <p className="text-muted-foreground text-sm">No connected clients.</p>
        ) : (
          <table className="w-full table-fixed text-sm">
            <thead>
              <tr className="border-b">
                <th className="w-[22%] py-2 text-left font-medium sm:w-auto">
                  Device
                </th>
                <th className="w-[20%] py-2 text-left font-medium sm:w-auto">
                  Version
                </th>
                <th className="w-[18%] py-2 text-left font-medium sm:w-auto">
                  Session
                </th>
                <th className="w-[18%] py-2 text-left font-medium sm:w-auto">
                  Sync
                </th>
                <th className="w-[22%] py-2 text-left font-medium sm:w-auto">
                  Last Contact
                </th>
              </tr>
            </thead>
            <tbody>
              {clients.map((client) => (
                <tr key={client.client_id} className="border-b last:border-0">
                  <td
                    className="truncate py-2 pr-2"
                    title={parseUserAgent(client.useragent)}
                  >
                    {parseUserAgent(client.useragent)}
                  </td>
                  <td className="text-muted-foreground truncate py-2 pr-2">
                    {client.version || '—'}
                  </td>
                  <td className="py-2">
                    <SessionStatusBadge client={client} />
                  </td>
                  <td className="py-2">
                    <SyncStatusBadge client={client} />
                  </td>
                  <td className="py-2">
                    <LastContactCell date={client.last_contact} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}
